import React from 'react';
import {Box, Checkbox, Container, FormControlLabel, MenuItem, Paper, TextField, Typography} from '@mui/material';
import {DissolutionApplication, DissolutionDateBounds, dissolutionReasons} from '../assets/dissolution-application';
import AddressDetailsEditor from '../ui/renewal/address-details-editor';
import {DarkButton} from './buttons';

export const DissolutionForm = ({application, setApplication, dateBounds, onContinue, loading, certifiedCopyPrice}: {
    application: DissolutionApplication;
    setApplication: React.Dispatch<React.SetStateAction<DissolutionApplication>>;
    dateBounds: DissolutionDateBounds;
    onContinue: () => void;
    loading: boolean;
    certifiedCopyPrice: string;
}) => {
    const update = <K extends keyof DissolutionApplication>(field: K, value: DissolutionApplication[K]) => {
        setApplication(current => ({...current, [field]: value}));
    };
    const showCalendar = (event: React.MouseEvent<HTMLInputElement>) => event.currentTarget.showPicker?.();

    return <Box component="form" onSubmit={event => {event.preventDefault(); onContinue();}}>
        <Paper component="fieldset" disabled={loading} sx={{p: {xs: 2, sm: 3}, mt: 3, minWidth: 0, border: 0}}>
            <Typography variant="h5">Dissolution details</Typography>
            <TextField fullWidth required type="date" label="Date of dissolution" InputLabelProps={{shrink: true}}
                       value={application.effectiveDate} onChange={event => update('effectiveDate', event.target.value)} sx={{mt: 2}}
                       inputProps={{min: dateBounds.min, max: dateBounds.max, onClick: showCalendar}}/>
            <TextField fullWidth required select label="Authorization or event resulting in dissolution"
                       value={application.dissolutionReason} onChange={event => update('dissolutionReason', event.target.value)} sx={{mt: 2}}>
                {dissolutionReasons.map(reason => <MenuItem key={reason.value} value={reason.value}>{reason.label}</MenuItem>)}
            </TextField>
            {application.dissolutionReason === 'other' && <TextField fullWidth required multiline minRows={3}
                label="Reason for dissolution" value={application.otherReason} inputProps={{maxLength: 240}} sx={{mt: 2}}
                helperText={`${application.otherReason.length}/240 characters`}
                onChange={event => update('otherReason', event.target.value)}/>}
            <Typography variant="h6" sx={{mt: 3}}>Authorized person winding up the company</Typography>
            <TextField fullWidth required label="Legal name" value={application.signerName} sx={{mt: 2}} inputProps={{maxLength: 255}}
                       onChange={event => update('signerName', event.target.value)}/>
            <TextField fullWidth required label="Title or capacity" value={application.signerTitle} sx={{mt: 2}} inputProps={{maxLength: 255}}
                       onChange={event => update('signerTitle', event.target.value)}/>
            <AddressDetailsEditor initialAddress={application.signerAddress} onChange={(field, value) => {
                setApplication(current => ({...current, signerAddress: {...current.signerAddress, [field]: value}}));
            }}/>
            <Box sx={{mt: 3}}>
                <Typography variant="body2" color="text.secondary">Certificate of Status included.</Typography>
                <FormControlLabel sx={{mt: 1, ml: 0}}
                    control={<Checkbox checked={application.certifiedCopy} onChange={event => update('certifiedCopy', event.target.checked)}/>}
                    label={`Add Certified Copy of Articles of Dissolution (+${certifiedCopyPrice})`}/>
            </Box>
        </Paper>
        <Container maxWidth="sm" sx={{display: 'flex', flexDirection: 'column'}}>
            <DarkButton type="submit" disabled={loading} sx={{p: 2, mt: 3}} disableElevation variant="contained">Proceed to payment</DarkButton>
        </Container>
    </Box>;
};
